/* eslint-disable react/jsx-props-no-spreading */
import {
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

type PartnerSelectorProps = {
  value: Partner | undefined
  onChange: any;
  inputProps?: any;
};

const partnerFromMap = (id: string, name: string): Partner => ({ id, name } as Partner);

const PartnerSelector = ({
  value,
  onChange,
  inputProps = undefined,
}: PartnerSelectorProps) => {
  const apiClient = useApiClient();
  const [inputValue, setInputValue] = useState('');
  const partners = useFetchImmutable<{ [partnerId: string]: string }>(apiClient.partnersClient.getPartnerNames, [], true);

  const allOptions = useMemo(() => {
    if (!partners?.data || Object.keys(partners.data).length === 0) {
      return [];
    }
    return Object.entries(partners.data)
      .map(([id, name]) => partnerFromMap(id, name))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [partners?.data]);

  const options = useMemo(() => {
    const term = inputValue.trim().toLowerCase();
    if (!term) return [];
    return allOptions.filter((p) => p.name.toLowerCase().includes(term));
  }, [allOptions, inputValue]);

  const noOptionsText = partners.error
    ? 'An error occurred while fetching partners'
    : 'No matching partner found';

  return (
    <Autocomplete
      id="partner-selector"
      value={value ?? null}
      onChange={onChange}
      isOptionEqualToValue={(partner, val) => partner.id === val?.id}
      getOptionLabel={(partner) => partner.name}
      noOptionsText={noOptionsText}
      options={options}
      filterOptions={(x) => x}
      loading={partners.isLoading}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...inputProps}
        />
      )}
    />
  );
};

export default PartnerSelector;
